import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'; // Ensure Bootstrap CSS is imported
import { useNavigate } from 'react-router-dom';
import { Navbar, Nav } from 'react-bootstrap';

const BANNER_URL = 'https://iis.memphis.edu/wp-content/uploads/2025/03/cropped-UofM_horiz_rgb.jpg';
const HEALTH_API = '/health';

const AccessRequestPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
  const [ip, setIp] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [ipLoading, setIpLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  // Instance user counts (from /health)
  const [instanceCounts, setInstanceCounts] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch client IP from server (network IP)
    const fetchIp = async () => {
      setIpLoading(true);
      try {
        const res = await fetch(apiPrefix + '/api/check-ip-access', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: 'temp' })
        });
        const data = await res.json();
        setIp(data.ip || '');
        // If IP is approved, redirect to the Overleaf instance login
        if (data.hasAccess) {
          window.location.href = apiPrefix + '/login';
          return;
        }

      } catch (error) {
        console.error('Failed to auto-detect IP:', error);
        setIp('');
      } finally {
        setIpLoading(false);
      }
    };
    fetchIp();
  }, []);

  useEffect(() => {
    // Fetch server health
    const fetchHealth = async () => {
      setHealthLoading(true);
      try {
        const res = await fetch(apiPrefix + HEALTH_API);
        const data = await res.json();
        setHealth(data);
      } catch {
        setHealth(null);
      } finally {
        setHealthLoading(false);
      }
    };
    fetchHealth();
  }, []);

  useEffect(() => {
    if (!healthLoading && health && health.activeUsersPerInstance) {
      setInstanceCounts(health.activeUsersPerInstance);
    }
  }, [health, healthLoading]);

  // Helper to normalize IP to IPv4
  function normalizeIp(ip) {
    if (typeof ip === 'string' && ip.startsWith('::ffff:')) {
      return ip.replace('::ffff:', '');
    }
    return ip;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const normIp = normalizeIp(ip);
      // Check if user exists
      const userRes = await fetch(`${apiPrefix}/api/users?email=${encodeURIComponent(email)}`);
      let user = null;
      if (userRes.ok) {
        const users = await userRes.json();
        user = Array.isArray(users) ? users.find(u => u.email === email) : null;
      }
      if (user) {
        // User exists, check if IP is approved or pending (as objects)
        const approved = (user.approvedIps || []).some(obj => obj && normalizeIp(obj.ip) === normIp);
        const pending = (user.pendingIps || []).some(obj => obj && normalizeIp(obj.ip) === normIp);
        if (approved) {
          setMessage('Your IP is already approved. You can log in.');
          setLoading(false);
          return;
        } else if (pending) {
          setMessage('Your IP is pending approval. Please wait for admin approval.');
          setLoading(false);
          return;
        } else {
          // Add to pendingIps
          await fetch(`${apiPrefix}/api/users/${user._id}/approve-ip`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ip: normIp })
          });
          setMessage('Your IP has been submitted for approval. Please wait for admin approval.');
        }
      } else {
        // New user, submit access request
        const response = await fetch(apiPrefix + '/api/access-requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ip: normIp, email, password })
        });
        const data = await response.json();
        if (response.ok) {
          setMessage('Access request submitted successfully!');
          setEmail('');
          setPassword('');
          // Redirect to /unapproved to show status
          navigate('/unapproved');
        } else {
          setMessage(data.error || 'Failed to submit access request.');
        }
      }
    } catch (err) {
      setMessage('Error submitting access request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <header className="bg-white shadow-sm">
        <div className="container-fluid p-0">
          <img src={BANNER_URL} alt="Institute for Intelligent Systems Banner" className="img-fluid w-100" style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }} />
        </div>
      </header>
      <div className="container py-4">
        <div className="mb-4">
          <div className="card border-info shadow-sm">
            <div className="card-body d-flex align-items-center gap-3 flex-wrap">
              <span className="badge bg-info text-dark fs-6 px-3 py-2">Server Health</span>
              {healthLoading ? (
                <span className="text-secondary">Checking...</span>
              ) : health ? (
                <>
                  <span className="fw-bold text-success">{health.status === 'healthy' ? 'Healthy' : 'Unhealthy'}</span>
                  <span className="text-muted small ms-3">Uptime: {Math.floor(health.uptime)}s</span>
                  <span className="text-muted small ms-3">Instances: {health.overleafInstances}</span>
                  <span className="text-muted small ms-3">Current Target: {health.currentTarget}</span>
                  {instanceCounts && Object.keys(instanceCounts).length > 0 && (
                    <span className="text-muted small ms-3">
                      <b>Active Users/Instance:</b> {Object.entries(instanceCounts).map(([inst, cnt]) => `${inst}: ${cnt}`).join(', ')}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-danger">Unable to fetch health status</span>
              )}
            </div>
          </div>
        </div>
        <div className="d-flex flex-grow-1 justify-content-center align-items-center py-5">
          <div className="card shadow-lg p-4 p-md-5" style={{ maxWidth: '500px', width: '100%' }}>
            <h2 className="card-title text-center mb-3">Overleaf Load Balancer Access Request</h2>
            <p className="card-subtitle text-muted text-center mb-4">Request access to the Overleaf load balancer for the Institute of Intelligent Systems at the University of Memphis.</p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="ipAddress" className="form-label">Your IP Address</label>
                <input
                  type="text"
                  className="form-control"
                  id="ipAddress"
                  placeholder={ipLoading ? 'Detecting...' : 'Enter your IP address'}
                  value={ip}
                  onChange={e => setIp(e.target.value)}
                  disabled={ipLoading}
                  required
                />
                {ipLoading && <small className="text-muted">Auto-detecting your IP address...</small>}
                {!ipLoading && !ip && <small className="text-warning">IP auto-detection failed. Please enter your IP address manually.</small>}
                {!ipLoading && ip && <small className="text-muted"><strong>How do I find my IP?</strong> <a href="https://support.microsoft.com/en-us/windows/find-your-ip-address-in-windows-f21a9bbc-c582-55cd-35e0-73431160a1b9" target="_blank" rel="noopener noreferrer">Windows</a> | <a href="https://support.apple.com/guide/mac-help/find-your-computers-ip-address-mchlf01d5c97/mac" target="_blank" rel="noopener noreferrer">macOS</a> | <a href="https://help.gnome.org/gnome-help/net-findip.html" target="_blank" rel="noopener noreferrer">Ubuntu/GNOME</a></small>}
              </div>
              <div className="mb-3">
                <label htmlFor="emailAddress" className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  id="emailAddress"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  placeholder="Choose a password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary w-100 py-2"
                disabled={loading || !ip || !email || !password}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Request Access'
                )}
              </button>
            </form>
            {message && (
              <div className={`alert mt-4 text-center ${message.includes('successfully') ? 'alert-success' : 'alert-danger'}`} role="alert">
                {message}
              </div>
            )}
            <div className="d-flex flex-column align-items-center mt-3">
              <a href="../add-user-address" className="btn btn-outline-success mb-2">Existing User? Add My IP</a>
              <a href="../download" className="btn btn-outline-primary ms-2">Go to Download Page</a>
              <small className="text-muted text-center">
                You were redirected here because your IP address is not registered or has expired.<br />
                If you are a returning user, you may need to re-approve your current IP address.
              </small>
            </div>
          </div>
        </div>
      </div>
      <footer className="bg-white border-top shadow-sm mt-auto py-3">
        <div className="container text-center">
          <a href="../portal-admin" className="btn btn-outline-primary">Admin Portal</a>
        </div>
      </footer>
    </div>
  );
};

export default AccessRequestPage;
